import { PlanKey } from '@kimuno/api-sdk/lib/types/requests/Billing';

export interface StripePlanBenefitsFeature {
  text: string;
  isHighlighted?: boolean;
}

export interface StripePlanBenefitsSection {
  title: string;
  features: StripePlanBenefitsFeature[];
}

export interface StripePlanDisplay {
  packageName: string;
  description: string;
  fullBenefits: StripePlanBenefitsSection[];
  bonusBenefits: StripePlanBenefitsSection[];
}

// Per-plan visual content for the Stripe upgrade flow. Strings are translation
// keys (passed through `t()` in components). `fullBenefits` is what the
// current-plan card renders; `bonusBenefits` is the "Everything you currently
// have, plus:" slice the upgrade card renders below its CTA.
//
// The catalogue mirrors the existing `/premium` page's `meinUnterricht Pro`,
// `meinUnterricht 360`, etc. cards verbatim so the Stripe dialog stays visually
// consistent with the public pricing flow.
export const STRIPE_PLAN_DISPLAY: Record<PlanKey, StripePlanDisplay> = {
  basis: {
    packageName: 'meinUnterricht Basis',
    description: 'stripeUpgrade.benefits.free',
    fullBenefits: [
      {
        title: 'stripeUpgrade.benefits.library',
        features: [
          { text: 'Limited access to materials', isHighlighted: true },
        ],
      },
    ],
    bonusBenefits: [],
  },
  pro: {
    packageName: 'meinUnterricht Pro',
    description: 'Library + Tools + Professional Development',
    fullBenefits: [
      {
        title: 'stripeUpgrade.benefits.library',
        features: [
          { text: '150.000+ teaching materials', isHighlighted: true },
          { text: '9.000+ learning videos' },
          { text: '100 downloads per month' },
          { text: 'Materials from leading educational publishers' },
          { text: 'New materials every week' },
          { text: 'Personal collections' },
        ],
      },
      {
        title: 'Tools',
        features: [
          { text: 'AI-Assistent Muki', isHighlighted: true },
          { text: '100 inquiries per month' },
          { text: 'Directly applicable to materials' },
          {
            text: 'Create quizzes, introductions, lesson plans and much more with one click',
          },
        ],
      },
      {
        title: 'Professional development',
        features: [
          { text: '1 course per month', isHighlighted: true },
          { text: '500+ practice-oriented courses' },
          { text: 'Certificates for completed courses' },
          { text: 'Unlimited access to live webinars' },
        ],
      },
    ],
    bonusBenefits: [
      {
        title: 'stripeUpgrade.benefits.library',
        features: [
          { text: '150.000+ teaching materials', isHighlighted: true },
          { text: '100 downloads per month' },
          { text: 'Materials from leading educational publishers' },
          { text: 'Personal collections' },
        ],
      },
      {
        title: 'Tools',
        features: [
          { text: 'AI-Assistent Muki', isHighlighted: true },
          { text: '100 inquiries per month' },
          { text: 'Directly applicable to materials' },
        ],
      },
      {
        title: 'Professional development',
        features: [
          { text: '1 course per month', isHighlighted: true },
          { text: '500+ practice-oriented courses' },
          { text: 'Certificates for completed courses' },
        ],
      },
    ],
  },
  courseOnly: {
    packageName: 'meinFortbildung',
    description: 'Only professional development',
    fullBenefits: [
      {
        title: 'Professional development',
        features: [
          { text: 'Unlimited access to courses', isHighlighted: true },
          { text: '500+ practice-oriented courses' },
          { text: 'Certificates for completed courses' },
          { text: 'Unlimited access to live webinars' },
        ],
      },
    ],
    bonusBenefits: [
      {
        title: 'Professional development',
        features: [
          { text: 'Unlimited access to courses', isHighlighted: true },
          { text: '500+ practice-oriented courses' },
          { text: 'Certificates for completed courses' },
          { text: 'Unlimited access to live webinars' },
        ],
      },
    ],
  },
  aiOnly: {
    // SM-385: finalized customer-facing label is `meinUnterricht KI` (not
    // `Muki` — that's the feature name inside the plan, not the plan name).
    // Mirrors `@kimuno/billing`'s `PLAN_LABELS.aiOnly`.
    packageName: 'meinUnterricht KI',
    description: 'Only AI Assistant',
    fullBenefits: [
      {
        title: 'Tools',
        features: [
          { text: 'AI-Assistent Muki', isHighlighted: true },
          { text: '100 inquiries per month' },
          { text: 'Directly applicable to materials' },
        ],
      },
    ],
    bonusBenefits: [
      {
        title: 'Tools',
        features: [
          { text: 'AI-Assistent Muki', isHighlighted: true },
          { text: '100 inquiries per month' },
          { text: 'Directly applicable to materials' },
        ],
      },
    ],
  },
  fullAccess: {
    packageName: 'meinUnterricht 360',
    description: 'Everything in Pro + Unlimited professional development',
    fullBenefits: [
      {
        title: 'stripeUpgrade.benefits.library',
        features: [
          { text: '200 downloads per month', isHighlighted: true },
          { text: '150.000+ teaching materials' },
          { text: 'Materials from leading educational publishers' },
          { text: 'Personal collections' },
        ],
      },
      {
        title: 'Tools',
        features: [
          { text: '200 inquiries per month', isHighlighted: true },
          { text: 'AI-Assistent Muki' },
          { text: 'Directly applicable to materials' },
        ],
      },
      {
        title: 'Professional development',
        features: [
          { text: 'Unlimited access to courses', isHighlighted: true },
          { text: '500+ practice-oriented courses' },
          { text: 'Certificates for completed courses' },
        ],
      },
    ],
    bonusBenefits: [
      {
        title: 'stripeUpgrade.benefits.library',
        features: [
          { text: '200 downloads per month', isHighlighted: true },
        ],
      },
      {
        title: 'Tools',
        features: [
          { text: '200 inquiries per month', isHighlighted: true },
        ],
      },
      {
        title: 'Professional development',
        features: [
          { text: 'Unlimited access to courses', isHighlighted: true },
        ],
      },
    ],
  },
};

export interface StripeSchoolLicenseSection {
  title: string;
  features: { text: string }[];
}

export const STRIPE_SCHOOL_LICENSE_DISPLAY = {
  packageName: 'meinUnterricht Schule',
  description: 'For institutions',
  sections: [
    {
      title: 'Access',
      features: [
        { text: 'A personal access for each teacher' },
        { text: 'Unlimited online reading' },
        { text: 'Download 100 materials per month per teacher' },
      ],
    },
    {
      title: 'Costs',
      features: [
        { text: 'Up to 75% discount for schools' },
        { text: 'No private costs for teachers' },
      ],
    },
    {
      title: 'Funding',
      features: [
        { text: 'Usually covered by the school authority' },
        {
          text: 'Easily fundable through regional and nationwide school development programs',
        },
      ],
    },
  ] as StripeSchoolLicenseSection[],
};
